import { Box, Grid, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  DataGrid,
  type GridColDef,
  type GridRenderEditCellParams,
  type GridValueFormatter,
  useGridApiContext,
} from '@mui/x-data-grid';
import {
  getHoldingCategoryId,
  getHoldingCategoryOrder,
  getModelSetIndexByName,
  holdingsCategories,
} from 'pages/Cases/helpers/misc';
import { CaseMilestoneAction } from 'pages/Cases/hooks/useGetCases';
import React, { useEffect, useMemo, useState } from 'react';

type ModelSetsTableProps = {
  action: CaseMilestoneAction;
  isDraft?: boolean;
  modelSetName: string;
  slideMode?: boolean;
  groupHeaderHeight?: number;
  columnHeaderHeight?: number;
  rowHeight?: number;
  maxHeight?: number;
  fontSize?: number;
  strategyCount?: number;
  enableCollapse?: boolean;
  compactFontSize?: string;
};
import { ModelSetHolding } from 'pages/Conversations/components/SlidesEditor/hooks/types';

import SplitModelSetSlideTable from 'components/SplitModelSetSlideTable';

// main component

const MAX_ROWS_BEFORE_COMPACT = 10;

const getCellPadding = (count: number) => {
  const basePadding = 10;
  const minPadding = 2;
  const shrinkFactor = 1.4;

  const computedPadding =
    basePadding - (count - MAX_ROWS_BEFORE_COMPACT) * shrinkFactor;

  return `${Math.max(minPadding, computedPadding)}px`;
};

const ModelSetsTable = ({
  action,
  maxHeight,
  modelSetName,
  fontSize = 13,
  slideMode = false,
  columnHeaderHeight = 36,
  enableCollapse = false,
}: ModelSetsTableProps): JSX.Element => {
  const modelSetIndex = getModelSetIndexByName(modelSetName, action);
  const [modelSet] = useState<any>(() =>
    getInitialModelSet(action, modelSetIndex),
  );
  const [modelSetDraft] = useState<any>(() =>
    JSON.parse(JSON.stringify(modelSet)),
  );
  const [rows, setRows] = useState<ModelSetTableRow[]>([]);

  useEffect(() => {
    setRows(getRows(modelSetDraft));
  }, [modelSetDraft]);

  const allStrategyCount = calculateAllStrategyCount(modelSetDraft);

  const cellPadding = enableCollapse
    ? getCellPadding(allStrategyCount)
    : '10px';

  const getColumns = (modelSet: any) => {
    const baseColumns: GridColDef[] = [
      {
        field: 'strategy',
        headerName: 'Strategist',
        sortable: false,
        headerClassName: 'base-columns-header',
        editable: false,
        ...getColumnWidth('strategy'),
        renderCell: params => {
          return (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                '&:hover .delete-button': {
                  opacity: 1,
                },
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  fontSize: compactFontSize || fontSize,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
              >
                {params.value}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'profile',
        headerName: 'Profile',
        sortable: false,
        headerClassName: 'base-columns-header',
        headerAlign: 'center' as const,
        align: 'center' as const,
        editable: false,
        type: 'number' as const,
        cellClassName: 'profile-column',
        ...getColumnWidth('profile'),
      },
    ];

    const optionColumns: GridColDef[] = [];

    modelSet.portfolio_options.forEach((portfolioOptions: any) => {
      portfolioOptions.portfolios.forEach(
        (portfolio: any, index: number, portfolios: any[]) => {
          optionColumns.push({
            field: getOptionKey(
              portfolioOptions.option,
              portfolio.risk_profile,
            ),
            headerName: portfolioOptions.option,
            sortable: false,
            headerAlign: 'center' as const,
            align: 'center' as const,
            editable: false,
            type: 'number' as const,
            cellClassName:
              index === portfolios.length - 1 ? 'last-risk-profile-column' : '',
            ...getColumnWidth(''),
            renderHeader: () => portfolio.risk_profile,
            renderEditCell: (params: GridRenderEditCellParams) => {
              if (slideMode) {
                return params.formattedValue;
              } else if (params.row[params.field] !== undefined) {
                return (
                  <CustomEditCell
                    {...params}
                    compactFontSize={compactFontSize}
                  />
                );
              } else {
                return null;
              }
            },
            valueFormatter: (weight: GridValueFormatter) =>
              weight === undefined || Number(weight) === 0
                ? ''
                : `${Number(weight)}%`,
            preProcessEditCellProps: (params: any) => {
              const value = params.props.value;
              const hasError = isNaN(value) || value < 0 || value > 100;
              return { ...params.props, error: hasError };
            },
          });
        },
      );
    });

    return [...baseColumns, ...optionColumns];
  };

  const groupHeaderHeight = useMemo(() => {
    if (rows.length > 10 && rows.length <= 14) return 50;
    if (rows.length > 14) return 40;
    return 60;
  }, [rows.length]);

  const compactFontSize = useMemo(() => {
    if (rows.length > 10 && rows.length <= 14) return '98%';
    if (rows.length > 14 && rows.length <= 18) return '92%';
    if (rows.length > 18) return '80%';
    return fontSize;
  }, [rows.length]);

  const showSplitTable = useMemo(() => {
    return allStrategyCount > 17;
  }, [allStrategyCount, rows.length]);

  // console.log('showSplitTable', showSplitTable, allStrategyCount);
  return (
    <>
      {showSplitTable ? (
        <>
          <SplitModelSetSlideTable
            data={action}
            dataType="cases"
            modelSetName={modelSetName}
            slideMode={true}
            fontSize={12}
            maxHeight={542}
            enableCollapse={true}
            groupHeaderHeight={groupHeaderHeight}
            columnHeaderHeight={columnHeaderHeight}
          />
        </>
      ) : (
        <Grid
          id={`model-set-slide-table-${modelSetName}`}
          item
          xs={12}
          sx={{ paddingBottom: '16px' }}
        >
          <Box sx={{ width: '100%', maxHeight }}>
            <StyledDataGrid
              rows={[...rows]}
              columns={getColumns(modelSet)}
              columnGroupingModel={getColumnGroupingModel(
                modelSet,
                modelSetName,
              )}
              columnGroupHeaderHeight={groupHeaderHeight}
              columnHeaderHeight={columnHeaderHeight}
              disableColumnMenu
              disableColumnResize
              disableRowSelectionOnClick
              hideFooter
              getRowHeight={() => 'auto'}
              getCellClassName={params => `${getCellClassName(params)}}`}
              sx={{
                '& .MuiDataGrid-cell': {
                  fontSize: compactFontSize || fontSize,
                  padding: cellPadding,
                },
                '& .MuiDataGrid-columnHeaderTitleContainer': {
                  fontSize: compactFontSize || fontSize,
                },
              }}
            />
          </Box>
        </Grid>
      )}
    </>
  );
};

export default ModelSetsTable;

// keys helpers

const getOptionKey = (option: number, riskProfile: number) =>
  `option_${option}_riskprofile_${riskProfile}`;

// model set helpers

const getModelSet = (action: any, modelSetIndex: number) =>
  action?.value?.model_set_holdings[modelSetIndex];

const iterateModelSet = (modelSet: any, predicate: any) => {
  modelSet?.portfolio_options.forEach((portfolioOptions: any) => {
    portfolioOptions.portfolios.forEach((portfolio: any) => {
      portfolio.holdings.forEach((holding: any) => {
        predicate(portfolioOptions, portfolio, holding);
      });
    });
  });
};

// column helpers

const getColumnGroupingModel = (modelSet: any, modelSetName: string) =>
  modelSet?.portfolio_options.map((portfolioOptions: any) => ({
    groupId: `Option${portfolioOptions.option}`,
    headerName: `${modelSetName}. Option ${portfolioOptions.option}`,
    description: 'Client Risk Profile',
    headerAlign: 'center',
    renderHeaderGroup: HeaderGroup,
    children: portfolioOptions.portfolios.map((portfolio: any) => ({
      field: getOptionKey(portfolioOptions.option, portfolio.risk_profile),
      headerName: portfolio.risk_profile,
      renderHeader: () => portfolio.risk_profile.toString(),
    })),
  }));

const getColumnWidth = (field: string) => {
  if (field === 'full_model_name' || field === 'strategy') {
    return { minWidth: 200, flex: 1.5 };
  } else if (field === 'profile') {
    return { minWidth: 70, flex: 0.3 };
  } else {
    return { minWidth: 60, flex: 0.4 };
  }
};

// rows helpers

type ModelSetTableRow = {
  id: number | string;
  strategy: string;
  profile: number | null;
  rowClassName: string;
  order: number;
  [key: string]: number | string | null;
};

export const calculateAllStrategyCount = (
  modelSet: ModelSetHolding,
): number => {
  const uniqueStrategies = new Set<string>();
  iterateModelSet(
    modelSet,
    (portfolioOptions: any, portfolio: any, holding: any) => {
      uniqueStrategies.add(
        JSON.stringify({
          strategy: holding.strategy,
          profile: holding.profile,
          category_role: holding.category_role,
        }),
      );
    },
  );
  return uniqueStrategies.size;
};

const getRows = (modelSet: any): ModelSetTableRow[] => {
  const rows: any[] = [];

  // First collect all unique strategies
  const uniqueStrategies = new Set<string>();
  iterateModelSet(
    modelSet,
    (portfolioOptions: any, portfolio: any, holding: any) => {
      uniqueStrategies.add(
        JSON.stringify({
          strategy: holding.strategy,
          profile: holding.profile,
          category_role: holding.category_role,
        }),
      );
    },
  );

  // Create rows for each unique strategy
  uniqueStrategies.forEach(strategyJson => {
    const { strategy, profile, category_role } = JSON.parse(strategyJson);
    rows.push({
      id: rows.length,
      strategy,
      profile,
      rowClassName: `category-${getHoldingCategoryId(category_role)}`,
      order: getHoldingCategoryOrder(category_role),
    });
  });

  // Add weights for each option and risk profile
  iterateModelSet(
    modelSet,
    (portfolioOptions: any, portfolio: any, holding: any) => {
      const row = findRow(rows, holding.strategy, holding.profile)!;
      const weightKey = getOptionKey(
        portfolioOptions.option,
        portfolio.risk_profile,
      );

      // Set both normal and _prev values
      row[weightKey] = ensureWeight(holding.weight * 100);
      row[`${weightKey}_prev`] = ensureWeight(holding.weight * 100);
    },
  );

  // Ensure all rows have all options
  modelSet.portfolio_options.forEach((portfolioOptions: any) => {
    portfolioOptions.portfolios.forEach((portfolio: any) => {
      const weightKey = getOptionKey(
        portfolioOptions.option,
        portfolio.risk_profile,
      );
      rows.forEach(row => {
        if (row[weightKey] === undefined) {
          row[weightKey] = 0;
          row[`${weightKey}_prev`] = 0;
        }
      });
    });
  });

  return sortRows(rows) as ModelSetTableRow[];
};

const findRow = (rows: ModelSetTableRow[], strategy: string, profile: number) =>
  rows.find(row => row.strategy === strategy && row.profile === profile);

const sortRows = (rows: ModelSetTableRow[]) => {
  // sort rows by profile
  rows.sort((a, b) => (a.profile ?? Infinity) - (b.profile ?? Infinity));
  // sort rows by strategy
  rows.sort((a, b) => a.strategy.localeCompare(b.strategy));
  // sort rows by category role order
  rows.sort((a, b) => a.order - b.order);
  return rows;
};

// cell helpers

const CustomEditCell = (props: GridRenderEditCellParams) => {
  const { id, value, field, compactFontSize } = props;
  const api = useGridApiContext();

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <input
        autoFocus
        type="number"
        min={0}
        max={100}
        value={formatWeight(value as number).replace('%', '')}
        onChange={ev => {
          const newValue = ev.target.value === '' ? 0 : ev.target.valueAsNumber;
          api.current.setEditCellValue({
            id,
            field,
            value: ensureWeight(newValue),
          });
        }}
        style={{
          width: '55px',
          padding: '7px 4px 5px 4px',
          border: '1px solid #ddd',
          borderRadius: '6px',
          backgroundColor: '#fff',
          textAlign: 'center',
          fontSize: compactFontSize || '14px',
          fontWeight: 'bold',
          outline: 'none',
          fontFamily: 'Roboto',
        }}
      />
    </Box>
  );
};

const getCellClassName = (params: any) => {
  let className = '';
  if (params.id === 'totals') {
    className = 'total-row';
    const value = params.row[params.field];
    if (value !== 100) {
      className += ' total-error';
    }
  } else {
    if (params.field.startsWith('option')) {
      className += ' input-cell';
    }
    className += ` ${params.row.rowClassName}`;
  }

  if (params.row[params.field] !== undefined) {
    className += ' weight-cell';
  }

  return className;
};

// weights helpers

const ensureWeight = (value: number = 0, decimals: number = 2) => {
  const percentage = Math.max(0, Math.min(value, 100));
  return parseFloat(percentage.toFixed(decimals));
};

// components

const HeaderGroup = (params: any) => (
  <Box sx={{ textAlign: 'center', width: '100%' }}>
    <Typography
      variant="subtitle2"
      component="div"
      sx={{
        width: '100%',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        fontSize: '10px',
        textOverflow: 'ellipsis',
        lineHeight: 1.1,
      }}
    >
      {params.headerName}
    </Typography>
    <Typography
      variant="caption"
      component="div"
      sx={{
        fontSize: '9px',
        lineHeight: 1.1,
      }}
    >
      {params.description}
    </Typography>
  </Box>
);

const StyledDataGrid = styled(DataGrid)(({ theme }) => {
  return {
    ...getCategoryClassNames(),
    borderTop: 'none',
    borderRight: 'none',
    '& *': {
      outline: 'none!important',
      cursor: 'default!important',
    },
    '& svg': {
      display: 'none',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      color: '#666666',
      borderBottom: 'none!important',
    },
    '& .MuiDataGrid-columnHeader': {
      borderTop: `1px solid ${theme.palette.divider}`,
      borderRight: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      '&[data-field^="option"]': {
        backgroundColor: theme.palette.background.paper,
      },
    },
    '& .MuiDataGrid-cell': {
      borderRight: `1px solid ${theme.palette.divider}`,
    },

    '& .profile-column, & .last-risk-profile-column': {
      borderRightWidth: '3px',
    },
    '& input[type="number"]': {
      MozAppearance: 'textfield',
    },
  };
});

const getCategoryClassNames = () =>
  holdingsCategories.reduce((css, category) => {
    css[`& .category-${category.id}`] = {
      backgroundColor: `${category.color} !important;`,
    };
    return css;
  }, {});

const getInitialModelSet = (action: any, modelSetIndex: number) => {
  const originalModelSet = getModelSet(action, modelSetIndex);
  const fullModelSet = JSON.parse(JSON.stringify(originalModelSet));

  // Collect all unique strategies
  const allStrategies = new Set<string>();
  iterateModelSet(originalModelSet, (po: any, p: any, h: any) => {
    allStrategies.add(
      JSON.stringify({
        strategy: h.strategy,
        profile: h.profile,
        category_role: h.category_role,
        model_id: h.model_id,
      }),
    );
  });

  // Ensure all portfolio_options have all strategies
  fullModelSet?.portfolio_options?.forEach((portfolioOption: any) => {
    portfolioOption.portfolios?.forEach((portfolio: any) => {
      const existingStrategies = new Set(
        portfolio.holdings?.map((h: any) =>
          JSON.stringify({
            strategy: h.strategy,
            profile: h.profile,
            category_role: h.category_role,
            model_id: h.model_id,
          }),
        ),
      );

      allStrategies.forEach(strategyJson => {
        if (!existingStrategies.has(strategyJson)) {
          const strategy = JSON.parse(strategyJson);
          portfolio.holdings.push({
            strategy: strategy.strategy,
            profile: strategy.profile,
            category_role: strategy.category_role,
            weight: 0,
            model_id: strategy.model_id,
          });
        }
      });
    });
  });

  return fullModelSet;
};

const formatWeight = (weight: number | undefined): string => {
  return weight === undefined || Number(weight) === 0
    ? ''
    : `${Number(weight)}%`;
};
